import type { WelcomeVideoSource } from '@/shared/utils/welcomeVideoSource';
import { trackStorefrontEvent, type StorefrontEvent } from './storefrontEvents';
import { mergePlayedRanges } from '@/core/domain/entities/playedRanges';

function assetId(source: WelcomeVideoSource): string {
  if (source.provider === 'youtube') return `youtube:${source.videoId}`;
  const url = new URL(source.videoSrc, 'https://shop.motito.co');
  const upload = url.pathname.match(/processed\/([a-f0-9-]{36})\//i)?.[1];
  if (upload) return upload;
  // Identify other configured files without sending URLs/query strings.
  let hash = 2166136261;
  for (const char of `${url.host}${url.pathname}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return `file:${(hash >>> 0).toString(16)}`;
}

export type WelcomeVideoCloseReason = 'user_dismissed' | 'opt_out' | 'playlist_switch' | 'background' | 'competing_ui' | 'identity_change' | 'context_change' | 'page_exit' | 'lease_expired' | 'system';
export type WelcomeVideoEntryPoint = 'automatic' | 'replay' | 'playlist';
export type WelcomeVideoRouteCategory = 'homepage' | 'search' | 'category' | 'product' | 'collection' | 'brand' | 'unknown';
export interface WelcomeVideoTelemetryContext {
  videoId: string;
  mediaVersion: string;
  playlistRevision: number;
  position: number;
  appearanceNumber: number;
  routeCategory: WelcomeVideoRouteCategory;
}
export interface WelcomeVideoPlaybackSample {
  currentTime: number;
  paused: boolean;
  seeking: boolean;
  buffering: boolean;
  playbackRate: number;
  visible: boolean;
}

type TimedSample = WelcomeVideoPlaybackSample & { at: number };

export class WelcomeVideoTelemetry {
  private started = false;
  private ended = false;
  private reachedEnd = false;
  private failed = false;
  private closed = false;
  private milestones = new Set<number>();
  private watched = 0;
  private playedRanges: [number, number][] = [];
  private duration = 0;
  private activeSeconds = 0;
  private lastSummarySeconds = 0;
  private replayCount = 0;
  private lastSample?: TimedSample;
  private visibleSince: number | null = performance.now();
  private visibleMilliseconds = 0;
  private readonly videoId: string;
  private readonly playbackId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  constructor(
    private readonly source: WelcomeVideoSource,
    private readonly entryPoint: WelcomeVideoEntryPoint,
    private readonly context?: WelcomeVideoTelemetryContext,
  ) {
    this.videoId = context?.videoId ?? assetId(source);
    this.emit('welcome_video_opened');
  }

  private emit(event: StorefrontEvent, extra: Record<string, string | number | boolean> = {}) {
    trackStorefrontEvent(event, {
      video_id: this.videoId, playback_id: this.playbackId, provider: this.source.provider,
      entry_point: this.entryPoint, watched_seconds: Math.round(this.watched * 100) / 100,
      duration_seconds: Math.round(this.duration * 100) / 100,
      watch_measurement: this.source.provider === 'file',
      active_measurement: this.source.provider === 'file' && this.lastSample !== undefined,
      ...(this.source.provider === 'file' ? {
        active_seconds: Math.round(this.activeSeconds * 100) / 100,
        coverage_percent: this.duration > 0 ? Math.round(this.watched / this.duration * 10000) / 100 : 0,
      } : {}),
      replay_count: this.replayCount,
      reached_end: this.reachedEnd,
      ...(this.context ? {
        media_version: this.context.mediaVersion, playlist_revision: this.context.playlistRevision,
        position: this.context.position, appearance_number: this.context.appearanceNumber,
        route_category: this.context.routeCategory,
      } : {}),
      ...extra,
    });
  }

  request() { this.emit('welcome_video_play_requested'); }
  start(previouslyPlayed = false) {
    if (this.closed) return;
    if (this.ended) {
      this.ended = false;
      this.replayCount += 1;
      this.lastSample = undefined;
      this.emit('welcome_video_replayed');
    }
    if (!this.started) {
      this.started = true;
      if (previouslyPlayed && !this.ended && this.replayCount === 0) {
        this.replayCount = 1;
        this.emit('welcome_video_replayed');
      }
      this.emit('welcome_video_started');
    }
  }

  recordActivity(sample: WelcomeVideoPlaybackSample, at = performance.now()) {
    if (!this.started || this.closed || this.source.provider !== 'file') return;
    if (!Number.isFinite(at) || !Number.isFinite(sample.currentTime) || sample.currentTime < 0
      || !Number.isFinite(sample.playbackRate) || sample.playbackRate <= 0) return;
    const previous = this.lastSample;
    this.lastSample = { ...sample, at };
    if (!previous || previous.paused || previous.seeking || previous.buffering || !previous.visible || sample.seeking) return;
    const elapsed = (at - previous.at) / 1000;
    const mediaDelta = sample.currentTime - previous.currentTime;
    // A seek is not playback. Cap to wall time and confirmed media advancement;
    // pause/buffer boundaries can contribute only the portion actually played.
    if (elapsed <= 0 || mediaDelta < 0 || mediaDelta > elapsed * previous.playbackRate + 0.25) return;
    this.activeSeconds += Math.min(elapsed, mediaDelta / previous.playbackRate);
    if (this.activeSeconds - this.lastSummarySeconds >= 10) {
      this.lastSummarySeconds = this.activeSeconds;
      this.emit('welcome_video_watch_summary');
    }
  }

  recordRanges(ranges: [number, number][], duration: number) {
    if (!this.started || this.closed || this.source.provider !== 'file' || !Number.isFinite(duration) || duration <= 0) return;
    const merged = mergePlayedRanges([...this.playedRanges, ...ranges], duration);
    this.playedRanges = merged;
    this.duration = duration;
    this.watched = Math.min(duration, merged.reduce((sum, [start, end]) => sum + end - start, 0));
    for (const milestone of [25, 50, 75, 95]) {
      if (this.watched / duration * 100 >= milestone && !this.milestones.has(milestone)) {
        this.milestones.add(milestone);
        this.emit('welcome_video_progress', { progress_percent: milestone });
      }
    }
  }

  end() {
    if (this.started && !this.ended && !this.closed) {
      this.ended = true;
      this.reachedEnd = true;
      this.emit('welcome_video_ended');
    }
  }
  fail() { if (!this.failed) { this.failed = true; this.emit('welcome_video_failed'); } }
  suppress() { this.emit('welcome_video_suppressed'); }
  recordVisibility(visible: boolean) {
    if (this.closed) return;
    const now = performance.now();
    if (this.visibleSince !== null) this.visibleMilliseconds += Math.max(0, now - this.visibleSince);
    this.visibleSince = visible ? now : null;
  }

  close(reason: WelcomeVideoCloseReason = 'system') {
    if (this.closed) return;
    this.recordVisibility(false);
    this.closed = true;
    this.emit('welcome_video_dismissed', {
      close_reason: reason,
      visible_duration_ms: Math.min(86400000, Math.round(this.visibleMilliseconds)),
    });
  }
}
